import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { ArrowRightIcon, MailIcon, LinkedinIcon, InstagramIcon, ChevronDownIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
export function Home() {
  return <div className="w-full min-h-screen bg-white" data-id="element-1692">
      <Hero data-id="element-1693" />
      <Services data-id="element-1694" />
      <Philosophy data-id="element-1695" />
      <Contact data-id="element-1696" />
    </div>;
}
function Hero() {
  const {
    scrollY
  } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const navigate = useNavigate();
  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white" data-id="element-1697">
      {/* Subtle Green Light Effect - From Edges */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-1698">
        <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-500/[0.03] rounded-full blur-3xl" data-id="element-1699" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/[0.03] rounded-full blur-3xl" data-id="element-1700" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-500/[0.03] rounded-full blur-3xl" data-id="element-1701" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-teal-500/[0.03] rounded-full blur-3xl" data-id="element-1702" />
      </div>

      {/* Geometric Accent Elements */}
      <motion.div style={{
      y
    }} className="absolute inset-0 pointer-events-none" data-id="element-1703">
        <div className="absolute top-0 right-0 w-96 h-96 border-l-2 border-b-2 border-emerald-500/[0.03]" style={{
        transform: 'rotate(45deg)'
      }} data-id="element-1704" />
        <div className="absolute bottom-0 left-0 w-80 h-80 border-r-2 border-t-2 border-emerald-500/[0.03]" style={{
        transform: 'rotate(45deg)'
      }} data-id="element-1705" />
        <div className="absolute top-1/3 left-1/4 w-64 h-64 border border-emerald-500/[0.02]" style={{
        transform: 'rotate(30deg)'
      }} data-id="element-1706" />
      </motion.div>

      <motion.div style={{
      opacity
    }} className="relative z-10 text-center px-6 max-w-6xl" data-id="element-1707">
        {/* Angular Accent Line */}
        <motion.div initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} transition={{
        duration: 1,
        delay: 0.5
      }} className="relative h-[2px] w-32 mx-auto mb-16" data-id="element-1708">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500 to-transparent" style={{
          clipPath: 'polygon(0 0, 100% 0, 95% 100%, 5% 100%)'
        }} data-id="element-1709" />
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-1710">
          <motion.h1 className="text-7xl md:text-8xl lg:text-[10rem] font-extralight tracking-tighter text-stone-900 mb-8 leading-[0.9]" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-1711">
            Maria Elina
          </motion.h1>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="space-y-6" data-id="element-1712">
          <p className="text-xl md:text-2xl text-stone-600 font-light tracking-wide max-w-xl mx-auto" data-id="element-1713">
            Creative Manager, Designer and Artist for complex Marketing, Web,
            App, Architecture and Art projects
          </p>
        </motion.div>

        {/* Question and Buttons */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="mt-12" data-id="element-1714">
          <p className="text-lg text-stone-500 font-light mb-8" data-id="element-1715">
            What do you seek?
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-1716">
            {/* Outline Button - A specific service */}
            <motion.button onClick={scrollToServices} whileHover={{
            y: -3
          }} className="group inline-block relative" data-id="element-1717">
              {/* Outer border layer */}
              <div className="absolute inset-0 border-2 border-emerald-600 pointer-events-none" style={{
              clipPath: 'polygon(5% 0%, 100% 0%, 95% 100%, 0% 100%)'
            }} data-id="element-1718" />
              {/* Inner layer - outline variant, top-to-bottom fill */}
              <div className="relative px-6 py-2 bg-transparent overflow-hidden mx-3 my-1.5" style={{
              clipPath: 'polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)'
            }} data-id="element-1719">
                <div className="absolute inset-0 bg-emerald-600 translate-y-[-100%] group-hover:translate-y-0 transition-transform duration-300" data-id="element-1720" />
                <span className="text-lg font-light relative z-10 inline-flex items-center gap-2 text-emerald-600 group-hover:text-white transition-colors duration-300" data-id="element-1721">
                  A specific service
                  <ChevronDownIcon className="w-4 h-4" data-id="element-1722" />
                </span>
              </div>
            </motion.button>

            {/* Outline Button - A look around (horizontal fill) */}
            <motion.button onClick={() => navigate('/work')} whileHover={{
            y: -3
          }} className="group inline-block relative" data-id="element-1723">
              {/* Outer border layer */}
              <div className="absolute inset-0 border-2 border-emerald-600 pointer-events-none" style={{
              clipPath: 'polygon(5% 0%, 100% 0%, 95% 100%, 0% 100%)'
            }} data-id="element-1724" />
              {/* Inner layer - outline variant, horizontal fill */}
              <div className="relative px-6 py-2 bg-transparent overflow-hidden mx-3 my-1.5" style={{
              clipPath: 'polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)'
            }} data-id="element-1725">
                <div className="absolute inset-0 bg-emerald-600 translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-300" data-id="element-1726" />
                <span className="text-lg font-light relative z-10 inline-flex items-center gap-2 text-emerald-600 group-hover:text-white transition-colors duration-300" data-id="element-1727">
                  A look around
                  <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-1728" />
                </span>
              </div>
            </motion.button>
          </div>
        </motion.div>

        {/* Angular Accent Line */}
        <motion.div initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} transition={{
        duration: 1,
        delay: 1
      }} className="relative h-[2px] w-32 mx-auto mt-16" data-id="element-1729">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500 to-transparent" style={{
          clipPath: 'polygon(5% 0, 95% 0, 100% 100%, 0 100%)'
        }} data-id="element-1730" />
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 1.2
      }} className="mt-16 flex justify-center" data-id="element-1731">
          <motion.div animate={{
          y: [0, 10, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="text-emerald-500" data-id="element-1732">
            <ChevronDownIcon className="w-8 h-8" data-id="element-1733" />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Angular Corner Accents */}
      <div className="absolute top-0 left-0 w-32 h-32 border-t-2 border-l-2 border-emerald-500/[0.05]" data-id="element-1734" />
      <div className="absolute top-0 right-0 w-32 h-32 border-t-2 border-r-2 border-emerald-500/[0.05]" data-id="element-1735" />
      <div className="absolute bottom-0 left-0 w-32 h-32 border-b-2 border-l-2 border-emerald-500/[0.05]" data-id="element-1736" />
      <div className="absolute bottom-0 right-0 w-32 h-32 border-b-2 border-r-2 border-emerald-500/[0.05]" data-id="element-1737" />
    </section>;
}
function Services() {
  const services = [{
    title: 'Design Direction',
    description: 'Strategic creative vision that brings multidisciplinary projects to life with cohesion and impact',
    icon: 'https://storage.yandexcloud.net/maria-images/images/design direction.svg',
    link: '/creative-direction'
  }, {
    title: 'Project Management',
    description: 'End-to-end project coordination ensuring timely delivery and seamless collaboration across teams',
    icon: 'https://storage.yandexcloud.net/maria-images/images/Project management.svg',
    link: '/project-management'
  }, {
    title: 'Branding + Marketing',
    description: 'Brand development and marketing campaigns that resonate with your audience and drive results',
    icon: 'https://storage.yandexcloud.net/maria-images/images/Marketing.svg',
    link: '/branding-marketing'
  }, {
    title: 'UX/UI',
    description: 'Human-centered digital experiences that blend aesthetics with intuitive interaction',
    icon: 'https://storage.yandexcloud.net/maria-images/images/UI.svg',
    link: '/ux-ui-design'
  }, {
    title: 'Architecture Concept',
    description: 'Spatial design and planning that harmonizes form, function, heritage and innovation',
    icon: 'https://storage.yandexcloud.net/maria-images/images/Architecture.svg',
    link: '/architecture'
  }, {
    title: 'Fine Art',
    description: 'Visual expression and commissions focused on the human form, feeling and experience',
    icon: 'https://storage.yandexcloud.net/maria-images/images/Art.svg',
    link: '/art'
  }];
  return <section id="services" className="relative py-32 px-6 bg-white overflow-hidden" data-id="element-1738">
      {/* Diagonal Section Divider */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-stone-50" style={{
      clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 100%)'
    }} data-id="element-1739" />

      {/* Angular Grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]" data-id="element-1740">
        <div className="absolute inset-0" style={{
        backgroundImage: `
              linear-gradient(30deg, #059669 1px, transparent 1px),
              linear-gradient(-30deg, #059669 1px, transparent 1px)
            `,
        backgroundSize: '120px 120px'
      }} data-id="element-1741" />
      </div>

      <div className="max-w-7xl mx-auto relative" data-id="element-1742">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-24" data-id="element-1743">
          <div className="relative inline-block" data-id="element-1744">
            <h2 className="text-5xl md:text-6xl font-extralight text-stone-900 mb-6 tracking-tight" data-id="element-1745">
              Services
            </h2>
            {/* Angular underline */}
            <motion.div initial={{
            scaleX: 0
          }} whileInView={{
            scaleX: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="absolute -bottom-2 left-0 right-0 h-[2px] bg-emerald-600" style={{
            clipPath: 'polygon(10% 0, 90% 0, 100% 100%, 0 100%)'
          }} data-id="element-1746" />
          </div>
          <p className="text-xl text-stone-600 font-light max-w-2xl mx-auto mt-8" data-id="element-1747">
            Comprehensive creative solutions tailored to your needs
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1748">
          {services.map((service, index) => <ServiceCard key={service.title} service={service} index={index} data-id="element-1749" />)}
        </div>
      </div>
    </section>;
}
function ServiceCard({
  service,
  index
}: {
  service: {
    title: string;
    description: string;
    icon: string;
    link?: string;
  };
  index: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const cardContent = <>
      {/* Holographic gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/10 via-teal-400/10 to-cyan-400/10" data-id="element-1750" />
      <div className="absolute inset-0 bg-gradient-to-tr from-cyan-300/5 via-emerald-300/5 to-teal-300/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-1751" />

      <div className="relative z-10 p-10 h-full flex flex-col" data-id="element-1752">
        <motion.div className="text-5xl mb-6" whileHover={{
        scale: 1.1,
        rotate: 5
      }} transition={{
        type: 'spring',
        stiffness: 300
      }} data-id="element-1753">
          {service.icon.startsWith('http') ? <img src={service.icon} alt={service.title} className="w-12 h-12" data-id="element-1754" /> : service.icon}
        </motion.div>
        <h3 className="text-2xl font-light text-stone-900 mb-4 tracking-tight" data-id="element-1755">
          {service.title}
        </h3>
        <p className="text-stone-700 font-light leading-relaxed flex-grow" data-id="element-1756">
          {service.description}
        </p>
      </div>

      {/* Angular corner accents */}
      <div className="absolute top-0 right-0 w-16 h-16 border-t-2 border-r-2 border-emerald-600/0 group-hover:border-emerald-600/50 transition-colors duration-300" data-id="element-1757" />
      <div className="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-emerald-600/0 group-hover:border-emerald-600/50 transition-colors duration-300" data-id="element-1758" />
    </>;
  const className = 'group relative overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-300 h-full cursor-pointer';
  const clipStyle = {
    clipPath: 'polygon(0 0, 100% 0, 100% 95%, 95% 100%, 0 100%)'
  };
  if (service.link) {
    return <motion.div ref={ref} initial={{
      opacity: 0,
      y: 50
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 50
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} whileHover={{
      y: -8
    }} className="h-full" data-id="element-1759">
        <Link to={service.link} className={className + ' block'} style={clipStyle} data-id="element-1760">
          {cardContent}
        </Link>
      </motion.div>;
  }
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 50
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 50
  }} transition={{
    duration: 0.6,
    delay: index * 0.1
  }} whileHover={{
    y: -8
  }} className={className} style={clipStyle} data-id="element-1761">
      {cardContent}
    </motion.div>;
}
function Philosophy() {
  return <section className="relative py-20 px-6 bg-stone-50 overflow-hidden" data-id="element-1762">
      {/* Diagonal divider from previous section */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-white" style={{
      clipPath: 'polygon(0 0, 100% 50%, 100% 100%, 0 100%)'
    }} data-id="element-1763" />

      {/* Angular Grid Background */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]" data-id="element-1764">
        <div className="absolute inset-0" style={{
        backgroundImage: `
              linear-gradient(30deg, #059669 1px, transparent 1px),
              linear-gradient(-30deg, #059669 1px, transparent 1px)
            `,
        backgroundSize: '100px 100px'
      }} data-id="element-1765" />
      </div>

      {/* Geometric Accent Elements */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" data-id="element-1766">
        <div className="absolute top-20 right-20 w-96 h-96 border-2 border-emerald-600" style={{
        transform: 'rotate(45deg)'
      }} data-id="element-1767" />
        <div className="absolute bottom-20 left-20 w-80 h-80 border border-emerald-600" style={{
        transform: 'rotate(30deg)'
      }} data-id="element-1768" />
      </div>

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-1769">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center" data-id="element-1770">
          <div className="relative inline-block mb-12" data-id="element-1771">
            {/* Angular Accent Line Above */}
            <motion.div initial={{
            scaleX: 0
          }} whileInView={{
            scaleX: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 1,
            delay: 0.2
          }} className="relative h-[2px] w-32 mx-auto mb-8" data-id="element-1772">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500 to-transparent" style={{
              clipPath: 'polygon(0 0, 100% 0, 95% 100%, 5% 100%)'
            }} data-id="element-1773" />
            </motion.div>
            <h2 className="text-5xl md:text-6xl font-extralight text-stone-900 tracking-tight" data-id="element-1774">
              Design Philosophy
            </h2>
            {/* Angular corner accents */}
            <div className="absolute -top-8 -right-8 w-16 h-16 border-t-2 border-r-2 border-emerald-500/30" data-id="element-1775" />
            <div className="absolute -bottom-8 -left-8 w-16 h-16 border-b-2 border-l-2 border-emerald-500/30" data-id="element-1776" />
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="relative" data-id="element-1777">
            <p className="text-lg text-stone-700 font-light leading-relaxed mb-12 px-8" data-id="element-1778">
              Inspired by human aspirations and business reality, my work
              bridges the gap between the trendy and the practical, the
              innovative and the comfortable, creating spaces and experiences
              that feel unique and welcoming.
            </p>
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="text-2xl md:text-3xl text-stone-600 font-light leading-relaxed max-w-3xl mx-auto" data-id="element-1779">
            Every project is an opportunity to break the mold, enhance
            well-being, foster growth and create harmonious lasting impact
            across disciplines.
          </motion.p>
        </motion.div>
      </div>

      {/* Angular Corner Accents */}
      <div className="absolute top-32 left-0 w-32 h-32 border-t-2 border-l-2 border-emerald-500/20" data-id="element-1780" />
      <div className="absolute top-32 right-0 w-32 h-32 border-t-2 border-r-2 border-emerald-500/20" data-id="element-1781" />
      <div className="absolute bottom-8 left-0 w-32 h-32 border-b-2 border-l-2 border-emerald-500/20" data-id="element-1782" />
      <div className="absolute bottom-8 right-0 w-32 h-32 border-b-2 border-r-2 border-emerald-500/20" data-id="element-1783" />
    </section>;
}
function Contact() {
  return <section className="relative pt-0 pb-20 px-6 bg-stone-50 overflow-hidden" data-id="element-1784">
      {/* Diagonal divider */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-stone-50" style={{
      clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 50%)'
    }} data-id="element-1785" />

      {/* Angular Accent Line - At top edge */}
      <div className="relative z-10 pt-24" data-id="element-1786">
        <motion.div initial={{
        scaleX: 0
      }} whileInView={{
        scaleX: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        delay: 0.2
      }} className="relative h-[2px] w-32 mx-auto" data-id="element-1787">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500 to-transparent" style={{
          clipPath: 'polygon(0 0, 100% 0, 95% 100%, 5% 100%)'
        }} data-id="element-1788" />
        </motion.div>
      </div>

      {/* Subtle Green Light Effect - directly behind Get in Touch button */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" data-id="element-1789">
        <div className="w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" data-id="element-1790" />
      </div>

      {/* Angular grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.008]" data-id="element-1791">
        <div className="absolute inset-0" style={{
        backgroundImage: `
              linear-gradient(30deg, #059669 1px, transparent 1px),
              linear-gradient(-30deg, #059669 1px, transparent 1px)
            `,
        backgroundSize: '80px 80px'
      }} data-id="element-1792" />
      </div>

      {/* Geometric Accent Elements */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-1793">
        <div className="absolute top-1/4 right-1/4 w-64 h-64 border border-emerald-500/[0.03]" style={{
        transform: 'rotate(45deg)'
      }} data-id="element-1794" />
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 border-2 border-emerald-500/[0.03]" style={{
        transform: 'rotate(30deg)'
      }} data-id="element-1795" />
      </div>

      <div className="max-w-4xl mx-auto text-center relative z-10 pt-8" data-id="element-1796">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} data-id="element-1797">
          <h2 className="text-5xl md:text-6xl font-extralight mb-8 tracking-tight text-stone-900" data-id="element-1798">
            Let's Create Together
          </h2>
          <p className="text-xl text-stone-600 font-light mb-12 max-w-2xl mx-auto" data-id="element-1799">
            Open to opportunities and commissions across all creative spheres.
          </p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="mb-12" data-id="element-1800">
            <motion.a href="mailto:contact@maria-elina.com" whileHover={{
            y: -3
          }} className="group inline-block relative" data-id="element-1801">
              {/* Outer border layer */}
              <div className="absolute inset-0 border-2 border-emerald-600 pointer-events-none" style={{
              clipPath: 'polygon(5% 0%, 100% 0%, 95% 100%, 0% 100%)'
            }} data-id="element-1802" />
              {/* Inner filled layer */}
              <div className="relative px-6 py-2 bg-emerald-600 text-white overflow-hidden mx-3 my-1.5" style={{
              clipPath: 'polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)'
            }} data-id="element-1803">
                <div className="absolute inset-0 bg-emerald-700 translate-y-[100%] group-hover:translate-y-0 transition-transform duration-300" data-id="element-1804" />
                <span className="text-lg font-light relative z-10 inline-flex items-center gap-2" data-id="element-1805">
                  <MailIcon className="w-4 h-4" data-id="element-1806" />
                  Get in Touch
                </span>
              </div>
            </motion.a>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="flex justify-center gap-8 mb-16" data-id="element-1807">
            {[{
            icon: LinkedinIcon,
            href: 'https://www.linkedin.com/in/-maria--elina-'
          }, {
            icon: InstagramIcon,
            href: 'https://www.instagram.com/maria.spirals'
          }].map((social, index) => <motion.a key={index} href={social.href} whileHover={{
            scale: 1.2,
            y: -3
          }} className="text-stone-600 hover:text-emerald-600 transition-colors duration-300" data-id="element-1808">
                <social.icon className="w-6 h-6" data-id="element-1809" />
              </motion.a>)}
          </motion.div>

          {/* Angular Accent Line */}
          <motion.div initial={{
          scaleX: 0
        }} whileInView={{
          scaleX: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          delay: 0.8
        }} className="relative h-[2px] w-32 mx-auto mb-8" data-id="element-1810">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500 to-transparent" style={{
            clipPath: 'polygon(5% 0, 95% 0, 100% 100%, 0 100%)'
          }} data-id="element-1811" />
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 1
        }} className="text-stone-600 font-light text-sm" data-id="element-1812">
            © 2024 Maria Elina. All rights reserved.
          </motion.p>
        </motion.div>
      </div>

      {/* Angular corner accents */}
      <div className="absolute bottom-0 left-0 w-32 h-32 border-b-2 border-l-2 border-emerald-500/[0.1]" data-id="element-1813" />
      <div className="absolute bottom-0 right-0 w-32 h-32 border-b-2 border-r-2 border-emerald-500/[0.1]" data-id="element-1814" />
      <div className="absolute top-32 left-0 w-24 h-24 border-t-2 border-l-2 border-emerald-500/[0.07]" data-id="element-1815" />
      <div className="absolute top-32 right-0 w-24 h-24 border-t-2 border-r-2 border-emerald-500/[0.07]" data-id="element-1816" />
    </section>;
}